import { Constants, ProviderSummaryTypes } from '../common/Constants';
import { getLanguage } from '../frontends/ProviderSearch/context/Internationalization/helpers';
import { RecommendationPreferencesData } from './smartChoiceEvents';

/**
 * Builds the Smart Choice FAQ URL and propagates the active language via the
 * `locale` query param so the standalone FAQ page renders in the selected
 * language. Without this, the FAQ page cannot recover the language when opened
 * as a fresh (potentially cross-origin) navigation from the widget.
 *
 * Reads locale from localStorage (MYUHC_LOCALE) first, falls back to cookie-based
 * locale from getLanguage() checkj.
 */
export const buildFaqUrl = (
  baseUrl?: string,
  isWidget?: boolean | string
): string => {
  // Try localStorage first (MYUHC_LOCALE), then fallback to cookie-based locale
  const storageLocale = localStorage.getItem('MYUHC_LOCALE');
  const locale = storageLocale || getLanguage().locale;
  const params = new URLSearchParams();
  if (isWidget) {
    params.set('source', 'cex');
  }
  params.set('locale', locale);
  return `${baseUrl}/findcare/faq?${params.toString()}`;
};

export enum breakDownKeys {
  'quality' = 'Quality',
  'benefits' = 'Benefits',
  'convenience' = 'Convenience',
  'personalFit' = 'Personal fit',
  'verification' = 'Updated provider info',
}
export const RecommendationCriteriaMatch = {
  None: 'None',
  All: 'All',
  Some: 'Some',
  Most: 'Most',
};

export const ExperienceMatchLabel = {
  UNAVAILABLE: 'Unavailable',
  LOW: 'Low',
  MODERATE: 'Moderate',
  HIGH: 'High',
  VERY_HIGH: 'Very high',
};

const criteriaMatchDescriptions = {
  [RecommendationCriteriaMatch.None]: 'No Criteria Met',
  [RecommendationCriteriaMatch.All]: 'All Criteria Met',
  [RecommendationCriteriaMatch.Some]: 'Some Criteria Met',
  [RecommendationCriteriaMatch.Most]: 'Most Criteria Met',
};

const criteriaViewMatchStyle = {
  [RecommendationCriteriaMatch.None]: {
    backgroundColor: '$core.color.orange.10',
  },
  [RecommendationCriteriaMatch.Some]: {
    backgroundColor: '#F3F3F3',
  },
  [RecommendationCriteriaMatch.Most]: {
    backgroundColor: '#F0F9ED',
  },
  [RecommendationCriteriaMatch.All]: {
    backgroundColor: '#F0F9ED',
  },
};

const criteriaTextMatchStyle = {
  [RecommendationCriteriaMatch.None]: {
    color: '#C24E14',
  },
  [RecommendationCriteriaMatch.Some]: {
    color: '$core.color.neutral.80',
  },
  [RecommendationCriteriaMatch.Most]: {
    color: '$core.color.green.100',
  },
  [RecommendationCriteriaMatch.All]: {
    color: '$core.color.green.100',
  },
};

export const getMatchDescription = (match: string): string =>
  criteriaMatchDescriptions[match] ??
  criteriaMatchDescriptions[RecommendationCriteriaMatch.None];

export const getChipbackgroundColor = (match: string): string =>
  criteriaViewMatchStyle[match]?.backgroundColor ??
  criteriaViewMatchStyle[RecommendationCriteriaMatch.None].backgroundColor;

export const getChipTextColor = (match: string): string =>
  criteriaTextMatchStyle[match]?.color ??
  criteriaTextMatchStyle[RecommendationCriteriaMatch.None].color;

export const isSuppressPremiumSmartchoice = (
  suppressStates,
  state: string
): string =>
  suppressStates
    ?.replace(/[\s,]+/g, ',')
    ?.split(',')
    ?.includes(state)
    ? 'Y'
    : 'N';

export const removeSpecialCharactersUsingMatch = (str: string): string =>
  (str?.match(/[a-zA-Z0-9 ]/g) ?? [])?.join('');

export const calculateDaysDifference = (
  date1: Date | string,
  date2: Date | string
): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  const firstDate = new Date(date1).getTime();
  const secondDate = new Date(date2).getTime();
  const difference = Math.abs(firstDate - secondDate);
  return Math.ceil(difference / oneDay);
};

export const getMatchCount = (...args: boolean[]): number =>
  args.filter((i) => i).length;

export const countAllMeasures = (val: number, ...args: boolean[]): number => {
  args.forEach((i) => {
    if (i) {
      val = val + 1;
    }
  });
  return val;
};

export const checkMatch = (item): boolean => item === 'y';

export const getMatchText = (match: string, text: string): string =>
  match === 'All' || match === 'Most' || match === 'Some' ? text : '';

export const getLlmAffinityMatch = (
  llmAffinity: string,
  subtexts?: {
    unavailable?: string;
    low?: string;
    moderate?: string;
    high?: string;
  }
): { label: string; subText?: string; color: string } => {
  const parsedAffinity = Number.parseFloat(llmAffinity.trim());

  switch (true) {
    case !llmAffinity:
    case parsedAffinity < 3:
      return {
        label: ExperienceMatchLabel.UNAVAILABLE,
        subText: subtexts?.unavailable,
        color: 'inherit',
      };
    case parsedAffinity < 5:
      return {
        label: ExperienceMatchLabel.LOW,
        subText: subtexts?.low,
        color: '#C24E14',
      };
    case parsedAffinity < 7:
      return {
        label: ExperienceMatchLabel.MODERATE,
        subText: subtexts?.moderate,
        color: 'inherit',
      };
    case parsedAffinity <= 8:
      return {
        label: ExperienceMatchLabel.HIGH,
        subText: subtexts?.high,
        color: '#007000',
      };
    default:
      return {
        label: ExperienceMatchLabel.VERY_HIGH,
        subText: subtexts?.high,
        color: '#007000',
      };
  }
};

/**
 * Sets Smart Choice preferences in sessionStorage
 *
 * @param preferences - The preferences data from the API
 * @param options - Configuration options
 * @param options.isSuperUser - Whether the current user is a super user
 * @param options.enableSmartChoiceIntroPopup - Whether the intro popup feature is enabled
 *
 * @example
 * setSmartChoiceSessionStorage(preferences, {
 *   isSuperUser: false,
 *   enableSmartChoiceIntroPopup: true
 * });
 */
export const setSmartChoiceSessionStorage = (
  preferences: RecommendationPreferencesData,
  options: { isSuperUser?: boolean; enableSmartChoiceIntroPopup?: boolean }
): void => {
  const { isSuperUser = false, enableSmartChoiceIntroPopup = false } = options;

  if (!enableSmartChoiceIntroPopup) {
    return;
  }

  const { hasDismissedPopover, hasDismissedCoachMarkWeb } = preferences;

  // Handle hasDismissedPopover
  if (
    hasDismissedPopover !== undefined &&
    hasDismissedPopover !== null &&
    (sessionStorage.getItem('hasDismissedPopover') === null || isSuperUser)
  ) {
    sessionStorage.setItem('hasDismissedPopover', hasDismissedPopover);
  } else if (
    hasDismissedPopover !== undefined &&
    hasDismissedPopover === null &&
    (sessionStorage.getItem('hasDismissedPopover') === null || isSuperUser)
  ) {
    sessionStorage.setItem('hasDismissedPopover', 'n');
  }

  // Handle hasDismissedCoachMarkWeb
  if (
    hasDismissedCoachMarkWeb !== undefined &&
    (sessionStorage.getItem('hasDismissedCoachMarkWeb') === null || isSuperUser)
  ) {
    sessionStorage.setItem(
      'hasDismissedCoachMarkWeb',
      hasDismissedCoachMarkWeb
    );
  }
};

/**
 * Determines if the current context represents a facility
 * @param chipValue - The current chip category value (e.g., 'Facilities', 'Primary Care')
 * @param providerType - The provider type (e.g., 'Organization', 'Practitioner')
 * @returns true if chipValue is 'Facilities' AND providerType is 'Organization'
 */
export function isFacility(chipValue: string, providerType: string): boolean {
  return (
    chipValue === Constants.CHIPS_CATEGORIES.FACILITIES &&
    providerType === ProviderSummaryTypes.ORGANIZATION
  );
}

/**
 * Resolves the appropriate Lagoon message key based on provider context
 * @param baseKey - The base Lagoon key (e.g., 'CONVENIENCE_BREAKDOWN_DESCRIPTION')
 * @param chipValue - The current chip category value
 * @param providerType - The provider type
 * @returns Lagoon key with _FACILITY suffix if in facility context, otherwise base key
 */
export function getConvenienceLagoonKey(
  baseKey: string,
  chipValue: string,
  providerType: string
): string {
  if (isFacility(chipValue, providerType)) {
    return `${baseKey}_FACILITY`;
  }
  return baseKey;
}
